import React, { useState, useCallback, useEffect, PropsWithChildren } from 'react';

import { TouchableOpacity } from 'react-native';
import { FlexProps, MarginProps } from 'styled-system';

import { Colors } from 'theme/colors';
import { NakedIcon } from 'theme/icons';
import { CheckBoxWrapper, CheckBoxLabel, CheckBoxBase, CheckBoxContainer } from './styles';

const TICK_SCALE_FACTOR = 2.7;
const DEFAULT_TICK_SIZE = 13;

/**
 * CheckBox component
 *
 * @param props - CheckBoxProps
 */
export function CheckBox(props: PropsWithChildren<CheckBoxProps>) {
  const {
    checked = false,
    onChange,
    children,
    size,
    checkedColor,
    labelGap = '15px',
    ...containerStyleProps
  } = props;
  const [isChecked, setIsChecked] = useState(checked || false);
  const checkBoxSize = size ? `${size}px` : ['35px', '40px'];
  const tickSize = size ? size / TICK_SCALE_FACTOR : DEFAULT_TICK_SIZE;

  const toggleCheckbox = useCallback(() => {
    const toggledIsChecked = !isChecked;

    setIsChecked(toggledIsChecked);
    if (onChange) {
      onChange(toggledIsChecked);
    }
  }, [isChecked, onChange]);

  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);

  return (
    <CheckBoxWrapper {...containerStyleProps}>
      <CheckBoxContainer>
        <TouchableOpacity onPress={toggleCheckbox}>
          <CheckBoxBase
            height={checkBoxSize}
            width={checkBoxSize}
            isChecked={isChecked}
            checkedColor={checkedColor}>
            {isChecked && <NakedIcon name="tick" size={tickSize} color={Colors.white} />}
          </CheckBoxBase>
        </TouchableOpacity>
      </CheckBoxContainer>
      {children && <CheckBoxLabel ml={labelGap}>{children}</CheckBoxLabel>}
    </CheckBoxWrapper>
  );
}
/** CheckBox props */
interface CheckBoxProps extends FlexProps, MarginProps {
  /** The gap between the label and the checkbox */
  labelGap?: string;
  /** Color to fill the checkbox when it is checked */
  checkedColor?: string;
  /** Pixel value width and height of the checkbox */
  size?: number;
  /** Describes whether the checkbox is checked or not */
  checked?: boolean;
  /** Emit the current value of isCheckBoxChecked */
  onChange?(flag: boolean): void;
  /** Action for clearing the error */
  onClearError?(): void;
  /** Error message */
  error?: string;
}
